/**
 * FlareSearch Widget Styles
 * CSS injected into shadow DOM for complete style isolation
 */

// ============================================
// DROPDOWN MODE STYLES
// ============================================
export const styles = `
/* Base styles */
* {
	box-sizing: border-box;
	margin: 0;
	padding: 0;
}

/* Dropdown container */
.flaresearch-dropdown {
	position: relative;
	background: var(--fs-bg);
	border: 1px solid #e5e7eb;
	border-radius: var(--fs-radius);
	box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
	max-height: 480px;
	overflow: hidden;
	display: none;
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
	font-size: 14px;
	line-height: 1.5;
	color: var(--fs-text);
}

/* Results container */
.flaresearch-results {
	max-height: 400px;
	overflow-y: auto;
	padding: 4px;
}

/* Fitment summary line — shown at the bottom of dropdown results
   when fitments matched but were filtered from the compact view. */
.flaresearch-fitment-summary {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 10px 14px;
	margin: 4px;
	border-top: 1px solid #e5e7eb;
	font-size: 13px;
	color: #6b7280;
	cursor: pointer;
	border-radius: 6px;
	transition: background 0.15s;
}

.flaresearch-fitment-summary:hover {
	background: #f9fafb;
	color: #111;
}

.flaresearch-fitment-link {
	color: var(--fs-primary, #2563eb);
	font-weight: 600;
	white-space: nowrap;
	font-size: 12px;
}

/* Individual result */
.flaresearch-result {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 12px;
	text-decoration: none;
	color: inherit;
	border-radius: calc(var(--fs-radius) - 4px);
	transition: background-color 0.15s ease;
	position: relative;
}

.flaresearch-result:hover {
	background-color: #f3f4f6;
}

.flaresearch-result:active {
	background-color: #e5e7eb;
}

/* Result image */
.flaresearch-result-image {
	width: 56px;
	height: 56px;
	flex-shrink: 0;
	border-radius: 6px;
	overflow: hidden;
	background-color: #f3f4f6;
}

.flaresearch-result-image img {
	width: 100%;
	height: 100%;
	object-fit: contain;
	padding: 4px;
	box-sizing: border-box;
}

.flaresearch-no-image {
	width: 100%;
	height: 100%;
	background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
}

/* Result info */
.flaresearch-result-info {
	flex: 1;
	min-width: 0;
}

.flaresearch-result-title {
	font-weight: 500;
	color: var(--fs-text);
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
}

.flaresearch-result-title mark {
	background-color: #fef08a;
	color: inherit;
	padding: 0 2px;
	border-radius: 2px;
}

.flaresearch-result-sku {
	font-size: 11px;
	color: #6b7280;
	margin-top: 2px;
	font-family: ui-monospace, "SF Mono", Menlo, monospace;
}

.flaresearch-result-brand {
	color: var(--fs-primary);
	font-family: ui-sans-serif, system-ui, sans-serif;
	font-weight: 500;
}

/* Section headers (dropdown mode) */
.flaresearch-section {
	margin-bottom: 12px;
}

.flaresearch-section-header {
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: #9ca3af;
	padding: 8px 16px 4px 16px;
	display: flex;
	align-items: center;
	gap: 6px;
}

.flaresearch-section-count {
	background: #f3f4f6;
	padding: 1px 6px;
	border-radius: 999px;
	font-size: 9px;
}

.flaresearch-section-items {
	display: flex;
	flex-direction: column;
}

/* Compact article/page/category/brand cards in dropdown mode */
.flaresearch-result-article,
.flaresearch-result-category,
.flaresearch-result-brand-row {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 16px;
	text-decoration: none;
	color: var(--fs-text);
	cursor: pointer;
	border: none;
	background: transparent;
	width: 100%;
	font-family: inherit;
	font-size: inherit;
	text-align: left;
}

.flaresearch-result-article:hover,
.flaresearch-result-category:hover,
.flaresearch-result-brand-row:hover {
	background: #f9fafb;
}

.flaresearch-result-icon {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	color: var(--fs-primary);
	background: #f3f4f6;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.flaresearch-result-icon svg {
	width: 16px;
	height: 16px;
}

.flaresearch-result-excerpt {
	font-size: 11px;
	color: #6b7280;
	line-height: 1.3;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.flaresearch-result-meta {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 4px;
	flex-wrap: wrap;
}

.flaresearch-result-price {
	font-size: 13px;
	font-weight: 600;
	color: var(--fs-primary);
}

.flaresearch-result-price-sale {
	display: flex;
	gap: 6px;
	align-items: baseline;
}

.flaresearch-result-price-sale .flaresearch-result-price-was {
	font-size: 12px;
	color: #9ca3af;
	text-decoration: line-through;
	font-weight: 400;
}

.flaresearch-result-price-sale .flaresearch-result-price-now {
	font-size: 13px;
	color: #dc2626;
	font-weight: 700;
}

.flaresearch-badge {
	font-size: 10px;
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 4px;
	text-transform: uppercase;
	letter-spacing: 0.3px;
}

.flaresearch-badge-oos {
	color: #991b1b;
	background: #fee2e2;
}

.flaresearch-result-oos .flaresearch-result-image {
	opacity: 0.5;
}

/* AI badge */
.flaresearch-ai-badge {
	position: absolute;
	top: 8px;
	right: 8px;
	font-size: 10px;
	font-weight: 600;
	color: #7c3aed;
	background-color: #ede9fe;
	padding: 2px 6px;
	border-radius: 4px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

/* Loading state */
.flaresearch-loading {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	padding: 24px;
	color: #6b7280;
}

.flaresearch-spinner {
	width: 20px;
	height: 20px;
	border: 2px solid #e5e7eb;
	border-top-color: var(--fs-primary);
	border-radius: 50%;
	animation: flaresearch-spin 0.8s linear infinite;
}

@keyframes flaresearch-spin {
	to {
		transform: rotate(360deg);
	}
}

/* Empty state */
.flaresearch-empty {
	display: none;
	padding: 20px;
	text-align: center;
	color: #6b7280;
}

.flaresearch-empty .fs-empty-inner {
	max-width: 100%;
}

.flaresearch-empty .fs-empty-icon {
	width: 36px;
	height: 36px;
	color: #d1d5db;
	margin-bottom: 8px;
}

.flaresearch-empty .fs-empty-headline {
	font-size: 14px;
	color: var(--fs-text);
	margin: 0 0 4px 0;
}

.flaresearch-empty .fs-empty-headline strong {
	color: var(--fs-primary);
	font-weight: 600;
}

.flaresearch-empty .fs-hint {
	font-size: 12px;
	margin: 0;
}

.flaresearch-empty .fs-empty-clear {
	margin-top: 8px;
	padding: 6px 12px;
	background: var(--fs-primary);
	color: white;
	border: none;
	border-radius: 6px;
	font-size: 12px;
	cursor: pointer;
}

.flaresearch-empty .fs-suggestions {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	justify-content: center;
	margin-top: 12px;
}

.flaresearch-empty .fs-suggestion-chip {
	padding: 4px 10px;
	background: #f3f4f6;
	border: 1px solid #e5e7eb;
	border-radius: 999px;
	font-size: 11px;
	color: var(--fs-text);
	cursor: pointer;
}

.flaresearch-empty .fs-suggestion-chip:hover {
	background: var(--fs-primary);
	color: white;
	border-color: var(--fs-primary);
}

.flaresearch-empty .fs-suggestion-chip-cat::before {
	content: "# ";
	color: #9ca3af;
}

/* Scrollbar styling */
.flaresearch-results::-webkit-scrollbar {
	width: 6px;
}

.flaresearch-results::-webkit-scrollbar-track {
	background: transparent;
}

.flaresearch-results::-webkit-scrollbar-thumb {
	background-color: #d1d5db;
	border-radius: 3px;
}

.flaresearch-results::-webkit-scrollbar-thumb:hover {
	background-color: #9ca3af;
}

/* Quick view button */
.flaresearch-quick-view {
	position: absolute;
	right: 12px;
	top: 50%;
	transform: translateY(-50%);
	width: 32px;
	height: 32px;
	border: none;
	background: var(--fs-primary);
	color: white;
	border-radius: 6px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	transition: opacity 0.15s;
}

.flaresearch-result:hover .flaresearch-quick-view {
	opacity: 1;
}

.flaresearch-quick-view:hover {
	filter: brightness(1.1);
}

.flaresearch-quick-view svg {
	width: 16px;
	height: 16px;
}

/* Recent searches */
.flaresearch-recent-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px 12px 6px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #6b7280;
}

.flaresearch-clear-recent {
	background: none;
	border: none;
	color: var(--fs-primary);
	cursor: pointer;
	font-size: 11px;
	font-weight: 500;
	text-transform: none;
	letter-spacing: normal;
}

.flaresearch-clear-recent:hover {
	text-decoration: underline;
}

.flaresearch-recent-item {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 10px 12px;
	text-align: left;
	background: none;
	border: none;
	cursor: pointer;
	color: var(--fs-text);
	font-size: 14px;
	border-radius: calc(var(--fs-radius) - 4px);
	transition: background-color 0.15s;
}

.flaresearch-recent-item:hover {
	background-color: #f3f4f6;
}

.flaresearch-recent-icon {
	width: 16px;
	height: 16px;
	color: #9ca3af;
	flex-shrink: 0;
}
`

// ============================================
// FULLSCREEN MODE STYLES
// ============================================
export const fullscreenStyles = `
/* Base styles */
* {
	box-sizing: border-box;
	margin: 0;
	padding: 0;
}

/* Overlay */
.fs-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: var(--fs-bg);
	z-index: 999999;
	display: flex;
	flex-direction: column;
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
	font-size: 14px;
	line-height: 1.5;
	color: var(--fs-text);
}

/* Header */
.fs-header {
	padding: 16px 24px;
	border-bottom: 1px solid #e5e7eb;
	background: var(--fs-bg);
}

.fs-search-container {
	max-width: 1200px;
	margin: 0 auto;
	display: flex;
	align-items: center;
	gap: 12px;
}

.fs-search-icon {
	width: 24px;
	height: 24px;
	color: #9ca3af;
	flex-shrink: 0;
}

.fs-search-input {
	flex: 1;
	padding: 12px 0;
	font-size: 18px;
	border: none;
	outline: none;
	background: transparent;
	color: var(--fs-text);
}

.fs-search-input::placeholder {
	color: #9ca3af;
}

.fs-close-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	background: transparent;
	cursor: pointer;
	border-radius: 8px;
	color: #6b7280;
	transition: all 0.15s;
}

.fs-close-btn:hover {
	background: #f3f4f6;
	color: var(--fs-text);
}

.fs-close-btn svg {
	width: 24px;
	height: 24px;
}

/* Body */
.fs-body {
	flex: 1;
	display: flex;
	overflow: hidden;
	max-width: 1400px;
	margin: 0 auto;
	width: 100%;
}

/* .fs-section-title is still used in the dropdown mode (legacy); the
   Doofinder-style fullscreen layout uses .fs-uppercase-title instead. */
.fs-section-title {
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #6b7280;
	margin-bottom: 12px;
}

/* Logo — top-left of fullscreen header when config.logoUrl is set.
   Width is capped so very wide wordmarks don't squeeze the input. */
.fs-logo {
	display: flex;
	align-items: center;
	flex-shrink: 0;
	text-decoration: none;
	height: 40px;
	max-width: 180px;
}
.fs-logo img {
	max-height: 40px;
	max-width: 180px;
	width: auto;
	height: auto;
	display: block;
	object-fit: contain;
}
.fs-search-icon-default {
	width: 24px;
	height: 24px;
	color: #9ca3af;
	flex-shrink: 0;
}

/* Recent-search chip bar — sits directly below the search row.
   Left-aligned with the container (same X as the logo) — matches
   Doofinder's actual layout. */
.fs-recent-bar {
	max-width: 1200px;
	margin: 10px auto 0;
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
}

.fs-recent-label {
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #6b7280;
	flex-shrink: 0;
}

.fs-recent-chips {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
	flex: 1;
	min-width: 0;
}

.fs-recent-chip {
	display: inline-flex;
	align-items: center;
	background: #f3f4f6;
	border: 1px solid #e5e7eb;
	border-radius: 999px;
	padding: 0 4px 0 12px;
	gap: 4px;
	font-size: 13px;
	line-height: 28px;
	color: var(--fs-text);
	transition: background-color 0.15s, border-color 0.15s;
}
.fs-recent-chip:hover { background: #e5e7eb; }

.fs-recent-chip-label {
	background: transparent;
	border: none;
	padding: 0;
	cursor: pointer;
	color: inherit;
	font: inherit;
	max-width: 220px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.fs-recent-chip-close {
	background: transparent;
	border: none;
	cursor: pointer;
	width: 20px;
	height: 20px;
	padding: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #6b7280;
	font-size: 16px;
	line-height: 1;
	border-radius: 50%;
	transition: background-color 0.12s, color 0.12s;
}
.fs-recent-chip-close:hover {
	background: #d1d5db;
	color: #111827;
}

.fs-recent-clear {
	background: transparent;
	border: none;
	cursor: pointer;
	color: var(--fs-primary);
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	padding: 4px 8px;
	flex-shrink: 0;
}
.fs-recent-clear:hover { text-decoration: underline; }

/* Tax label next to prices (e.g. "inc GST") */
.fs-result-price-tax {
	font-size: 0.75em;
	font-weight: 400;
	color: #6b7280;
	margin-left: 4px;
}

/* Search-as-you-type suggestion chips — live below the input when
   the user is typing. Non-removable (cf. fs-recent-chip which has an
   X button). Click fills the input and runs the search. */
.fs-suggest-bar {
	max-width: 1200px;
	margin: 10px auto 0;
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
}

.fs-suggest-chips {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
	flex: 1;
	min-width: 0;
}

.fs-suggest-chip {
	display: inline-flex;
	align-items: center;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 999px;
	padding: 6px 14px;
	font-size: 13px;
	line-height: 1;
	color: var(--fs-text);
	cursor: pointer;
	transition: border-color 0.12s ease, background-color 0.12s ease;
	font-family: inherit;
}
.fs-suggest-chip:hover {
	border-color: var(--fs-primary);
	color: var(--fs-primary);
}

/* Mobile drawer backdrop — shared between old + new layouts. */
.fs-sidebar-backdrop {
	display: none;
}

.fs-hint {
	font-size: 14px;
	color: #9ca3af;
	margin-top: 8px;
}

/* Results container — stack type-sections vertically (Doofinder style).
   The product cards themselves are gridded inside .fs-section-items. */
.fs-results-grid {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.fs-result-card {
	display: flex;
	flex-direction: column;
	padding: 12px;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	text-decoration: none;
	color: inherit;
	transition: all 0.15s;
	position: relative;
}

.fs-result-card:hover {
	border-color: var(--fs-primary);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.fs-result-image {
	width: 100%;
	aspect-ratio: 1;
	border-radius: 8px;
	overflow: hidden;
	background: #f3f4f6;
	margin-bottom: 12px;
	position: relative;
}

/* Quick view button for fullscreen mode */
.fs-quick-view {
	position: absolute;
	bottom: 8px;
	left: 50%;
	transform: translateX(-50%);
	padding: 8px 16px;
	border: none;
	background: var(--fs-primary);
	color: white;
	border-radius: 8px;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	font-weight: 600;
	opacity: 0;
	transition: opacity 0.15s, transform 0.15s;
	white-space: nowrap;
}

.fs-result-card:hover .fs-quick-view {
	opacity: 1;
}

.fs-quick-view:hover {
	filter: brightness(1.1);
	transform: translateX(-50%) scale(1.02);
}

.fs-quick-view svg {
	width: 14px;
	height: 14px;
}

.fs-result-image img {
	width: 100%;
	height: 100%;
	object-fit: contain;
	padding: 8px;
	box-sizing: border-box;
}

.fs-no-image {
	width: 100%;
	height: 100%;
	background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
}

.fs-result-info {
	flex: 1;
}

.fs-result-title {
	font-size: 13px;
	font-weight: 500;
	color: var(--fs-text);
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	line-height: 1.4;
}

.fs-result-title mark {
	background-color: #fef08a;
	color: inherit;
	padding: 0 2px;
	border-radius: 2px;
}

.fs-result-price {
	font-size: 14px;
	font-weight: 600;
	color: var(--fs-primary);
	margin-top: 8px;
}

.fs-ai-badge {
	position: absolute;
	top: 8px;
	right: 8px;
	font-size: 10px;
	font-weight: 600;
	color: #7c3aed;
	background-color: #ede9fe;
	padding: 2px 6px;
	border-radius: 4px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

/* Loading */
.fs-loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 60px;
	color: #6b7280;
}

.fs-spinner {
	width: 32px;
	height: 32px;
	border: 3px solid #e5e7eb;
	border-top-color: var(--fs-primary);
	border-radius: 50%;
	animation: fs-spin 0.8s linear infinite;
}

@keyframes fs-spin {
	to {
		transform: rotate(360deg);
	}
}

/* Section headers in result list */
.fs-section {
	margin-bottom: 24px;
}

.fs-section-header {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: #6b7280;
	margin-bottom: 10px;
	display: flex;
	align-items: center;
	gap: 8px;
	padding-bottom: 6px;
	border-bottom: 1px solid #e5e7eb;
}

.fs-section-count {
	background: #f3f4f6;
	padding: 2px 8px;
	border-radius: 999px;
	font-size: 10px;
	color: #6b7280;
}

.fs-section-items {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
	gap: 12px;
}

/* Article / page / category / brand cards */
.fs-result-card-article,
.fs-result-card-category,
.fs-result-card-brand-row {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 12px;
	background: white;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	text-decoration: none;
	color: var(--fs-text);
	cursor: pointer;
	font-family: inherit;
	font-size: inherit;
	text-align: left;
	width: 100%;
	transition: border-color 0.15s, box-shadow 0.15s;
}

.fs-result-card-article:hover,
.fs-result-card-category:hover,
.fs-result-card-brand-row:hover {
	border-color: var(--fs-primary);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.fs-result-card-icon {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	color: var(--fs-primary);
	background: #f3f4f6;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.fs-result-card-icon svg {
	width: 20px;
	height: 20px;
}

.fs-result-card-excerpt {
	font-size: 12px;
	color: #6b7280;
	margin-top: 4px;
	line-height: 1.4;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.fs-result-card-brand {
	font-weight: 500;
}

/* Make section grids handle small article cards better */
.fs-section:has(.fs-result-card-article),
.fs-section:has(.fs-result-card-category),
.fs-section:has(.fs-result-card-brand-row) .fs-section-items {
	grid-template-columns: 1fr;
}

/* Price range inputs live inside the Doofinder-style Price facet. */
.fs-filter-price-row {
	display: flex;
	align-items: center;
	gap: 6px;
}

.fs-filter-price-row input {
	width: 100%;
	min-width: 0;
	padding: 6px 8px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	font-size: 13px;
	background: white;
	color: var(--fs-text);
}

.fs-filter-price-row input:focus {
	outline: none;
	border-color: var(--fs-primary);
}

.fs-filter-price-row span {
	color: #9ca3af;
}

/* Result card meta + badges + sale pricing */
.fs-result-sku {
	font-size: 11px;
	color: #6b7280;
	margin-top: 2px;
	font-family: ui-monospace, "SF Mono", Menlo, monospace;
}

.fs-result-meta {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 6px;
	flex-wrap: wrap;
}

.fs-result-price-sale {
	display: flex;
	gap: 6px;
	align-items: baseline;
}

.fs-result-price-sale .fs-result-price-was {
	font-size: 12px;
	color: #9ca3af;
	text-decoration: line-through;
	font-weight: 400;
}

.fs-result-price-sale .fs-result-price-now {
	font-size: 14px;
	color: #dc2626;
	font-weight: 700;
}

.fs-badge {
	font-size: 10px;
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 4px;
	text-transform: uppercase;
	letter-spacing: 0.3px;
}

.fs-badge-oos {
	color: #991b1b;
	background: #fee2e2;
	position: absolute;
	top: 8px;
	left: 8px;
}

.fs-result-oos .fs-result-image img {
	opacity: 0.5;
}

/* In-search product detail view — slides in over the search results.
   The .fs-main container is position:relative so this can absolute-position
   on top of it without affecting the rest of the layout. */
.fs-product-view {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: var(--fs-bg, #ffffff);
	overflow-y: auto;
	padding: 24px 32px 100px 32px;
	z-index: 5;
	transform: translateX(40px);
	opacity: 0;
	transition: transform 0.28s ease, opacity 0.28s ease;
}

.fs-product-view.is-visible {
	transform: translateX(0);
	opacity: 1;
}

/* Fitment detail view — same overlay behaviour as product view */
.fs-fitment-view {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: var(--fs-bg, #ffffff);
	overflow-y: auto;
	padding: 24px 32px 100px 32px;
	z-index: 6;
	transform: translateX(40px);
	opacity: 0;
	transition: transform 0.28s ease, opacity 0.28s ease;
}

.fs-fitment-view.is-visible {
	transform: translateX(0);
	opacity: 1;
}

/* ─── Fitment detail view layout ─── */
.fs-fitment-detail {
	max-width: 920px;
	margin: 0 auto;
}

.fs-fd-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	margin-bottom: 24px;
	flex-wrap: wrap;
}

.fs-fd-header-left {
	display: flex;
	align-items: center;
	gap: 8px;
}

/* "New search" pill button — appears in fitment + product detail headers */
.fs-new-search {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px 8px 12px;
	border: 1px solid #e5e7eb;
	background: white;
	border-radius: 999px;
	cursor: pointer;
	font-size: 13px;
	font-weight: 500;
	color: var(--fs-text);
	font-family: inherit;
	transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.fs-new-search:hover {
	border-color: var(--fs-primary);
	background: var(--fs-primary);
	color: white;
}

.fs-new-search svg {
	width: 14px;
	height: 14px;
}

/* Product detail header — back button + new search on one row */
.fs-pv-header {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 20px;
	flex-wrap: wrap;
}

.fs-fitment-back {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px 8px 10px;
	border: 1px solid #e5e7eb;
	background: white;
	border-radius: 999px;
	cursor: pointer;
	font-size: 13px;
	font-weight: 500;
	color: var(--fs-text);
	font-family: inherit;
	transition: border-color 0.15s, background 0.15s;
}

.fs-fitment-back:hover {
	border-color: var(--fs-primary);
	background: #f9fafb;
}

.fs-fitment-back svg {
	width: 14px;
	height: 14px;
}

.fs-fd-link {
	font-size: 13px;
	color: var(--fs-primary);
	text-decoration: none;
	font-weight: 500;
}

.fs-fd-link:hover {
	text-decoration: underline;
}

.fs-fd-title-block {
	padding-bottom: 20px;
	border-bottom: 1px solid #e5e7eb;
	margin-bottom: 20px;
}

.fs-fd-title {
	font-size: 24px;
	font-weight: 600;
	line-height: 1.25;
	color: var(--fs-text);
	margin: 0 0 6px 0;
}

.fs-fitment-meta {
	color: #6b7280;
	font-size: 14px;
	margin: 0;
}

/* Plate schematic image above the parts list */
.fs-fd-plate {
	margin: 0 0 24px 0;
	padding: 16px;
	background: #fafafa;
	border: 1px solid #e5e7eb;
	border-radius: 10px;
	text-align: center;
	position: relative;
}

.fs-fd-plate a {
	display: inline-block;
	line-height: 0;
	cursor: zoom-in;
}

.fs-fd-plate img {
	max-width: 100%;
	max-height: 520px;
	height: auto;
	border-radius: 6px;
	background: white;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.fs-fd-plate-label {
	position: absolute;
	top: 12px;
	left: 12px;
	background: white;
	border: 1px solid #e5e7eb;
	border-radius: 999px;
	padding: 4px 12px;
	font-size: 11px;
	font-weight: 500;
	color: #6b7280;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

.fs-fd-plate-label strong {
	color: var(--fs-primary);
	font-weight: 700;
	margin-left: 4px;
}

/* Parts list — one row per part */
.fs-fp-list {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.fs-fp-row {
	display: grid;
	grid-template-columns: auto 56px 1fr auto;
	align-items: center;
	gap: 16px;
	padding: 10px 14px 10px 10px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	background: white;
	text-align: left;
	font-family: inherit;
	font-size: inherit;
	color: inherit;
	width: 100%;
	transition: border-color 0.15s, background 0.15s, transform 0.15s;
}

.fs-fp-row.is-linked {
	cursor: pointer;
}

/* Inner children of a linked row don't absorb clicks — makes the
   whole card area the hit target, so clicking the image/title/price
   reliably bubbles up to the row's click handler. */
.fs-fp-row.is-linked > * {
	pointer-events: none;
}

.fs-fp-row.is-linked:hover {
	border-color: var(--fs-primary);
	background: #f9fafb;
	transform: translateX(2px);
}

.fs-fp-row.is-unlinked {
	opacity: 0.6;
	background: #fafafa;
}

.fs-fp-plate {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 28px;
	height: 28px;
	padding: 0 8px;
	border-radius: 999px;
	background: #eef2ff;
	color: #4338ca;
	font-size: 12px;
	font-weight: 700;
	font-variant-numeric: tabular-nums;
}

.fs-fp-plate-empty {
	background: #f3f4f6;
	color: #9ca3af;
	font-weight: 500;
}

.fs-fp-image {
	width: 56px;
	height: 56px;
	border-radius: 6px;
	overflow: hidden;
	background: #f9fafb;
	display: flex;
	align-items: center;
	justify-content: center;
}

.fs-fp-image img {
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.fs-fp-image .fs-no-image {
	width: 100%;
	height: 100%;
	background: repeating-linear-gradient(
		45deg,
		#f3f4f6,
		#f3f4f6 4px,
		#e5e7eb 4px,
		#e5e7eb 8px
	);
}

.fs-fp-info {
	min-width: 0;
}

.fs-fp-title {
	font-size: 14px;
	font-weight: 500;
	color: var(--fs-text);
	line-height: 1.35;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.fs-fp-qty {
	color: #6b7280;
	font-size: 12px;
	margin-left: 6px;
	font-weight: 400;
}

.fs-fp-meta {
	font-size: 12px;
	color: #6b7280;
	margin-top: 2px;
	font-variant-numeric: tabular-nums;
}

.fs-fp-oem {
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.fs-fp-right {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 4px;
	flex-shrink: 0;
}

.fs-fp-price {
	font-size: 14px;
	font-weight: 600;
	color: var(--fs-text);
	white-space: nowrap;
}

.fs-fp-price-was {
	text-decoration: line-through;
	color: #9ca3af;
	font-weight: 400;
	font-size: 12px;
	margin-right: 6px;
}

.fs-fp-price-now {
	color: #dc2626;
	font-weight: 700;
	font-size: 14px;
}

.fs-part-unavailable {
	color: #9ca3af;
	font-style: italic;
	font-size: 12px;
}

/* Product detail — "Also known as" aliases */
.fs-pv-aliases {
	margin-top: 28px;
}

.fs-pv-alias-list {
	list-style: none;
	padding: 0;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.fs-pv-alias-list li {
	display: flex;
	flex-direction: column;
	padding: 10px 14px;
	background: #fafafa;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
}

.fs-pv-alias-name {
	font-size: 14px;
	font-weight: 500;
	color: var(--fs-text);
}

.fs-pv-alias-context {
	font-size: 12px;
	color: #6b7280;
	margin-top: 2px;
}

/* Product detail — manuals/PDFs */
.fs-pv-manuals {
	margin-top: 28px;
}

.fs-pv-manual-list {
	list-style: none;
	padding: 0;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.fs-pv-manual-list li a {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 14px;
	background: white;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	text-decoration: none;
	color: var(--fs-text);
	transition: border-color 0.15s, background 0.15s;
}

.fs-pv-manual-list li a:hover {
	border-color: var(--fs-primary);
	background: #f9fafb;
}

.fs-pv-manual-list svg {
	width: 16px;
	height: 16px;
	color: var(--fs-primary);
	flex-shrink: 0;
}

.fs-pv-manual-list span {
	font-size: 14px;
}

/* Product detail "Fits these bikes" section */
.fs-pv-fits {
	margin-top: 28px;
}

.fs-pv-fits-list {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.fs-pv-fit-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
	padding: 12px 16px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	background: white;
	text-decoration: none;
	color: inherit;
	cursor: pointer;
	transition: border-color 0.15s, background 0.15s, transform 0.15s;
}

.fs-pv-fit-item:hover {
	border-color: var(--fs-primary);
	background: #f9fafb;
	transform: translateX(2px);
}

.fs-pv-fit-text {
	min-width: 0;
	flex: 1;
}

.fs-pv-fit-model {
	font-size: 11px;
	font-weight: 600;
	color: var(--fs-primary);
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.fs-pv-fit-title {
	font-size: 14px;
	color: var(--fs-text);
	margin-top: 3px;
	line-height: 1.3;
}

.fs-pv-fit-arrow {
	width: 16px;
	height: 16px;
	color: #9ca3af;
	flex-shrink: 0;
}

.fs-pv-fit-item:hover .fs-pv-fit-arrow {
	color: var(--fs-primary);
}

.fs-product-loading,
.fs-product-error {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	min-height: 60vh;
	color: #6b7280;
}

.fs-product-error a {
	color: var(--fs-primary);
	text-decoration: underline;
	cursor: pointer;
}

.fs-product-back {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px 8px 10px;
	border: 1px solid #e5e7eb;
	background: white;
	border-radius: 999px;
	cursor: pointer;
	font-size: 13px;
	font-weight: 500;
	color: var(--fs-text);
	margin-bottom: 24px;
	transition: border-color 0.15s, background 0.15s;
}

.fs-product-back:hover {
	border-color: var(--fs-primary);
	background: #f9fafb;
}

.fs-product-back svg {
	width: 16px;
	height: 16px;
}

.fs-pv-body {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
	gap: 32px;
	margin-bottom: 24px;
}

@media (max-width: 768px) {
	.fs-pv-body {
		grid-template-columns: 1fr;
	}

	.fs-product-view {
		padding: 16px 16px 100px 16px;
	}
}

.fs-pv-image {
	width: 100%;
	background: #f9fafb;
	border-radius: 12px;
	overflow: hidden;
	aspect-ratio: 1;
	display: flex;
	align-items: center;
	justify-content: center;
}

.fs-pv-image img {
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.fs-pv-oos .fs-pv-image img {
	opacity: 0.6;
}

.fs-pv-info {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.fs-pv-title {
	font-size: 24px;
	font-weight: 600;
	line-height: 1.25;
	color: var(--fs-text);
	margin: 0;
}

.fs-pv-subtitle {
	font-size: 13px;
	color: #6b7280;
	font-family: ui-monospace, "SF Mono", Menlo, monospace;
}

.fs-pv-meta-row {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	margin-top: 4px;
}

.fs-pv-price-row {
	display: flex;
	align-items: baseline;
	gap: 8px;
}

.fs-pv-price {
	font-size: 28px;
	font-weight: 700;
	color: var(--fs-primary);
}

.fs-pv-price-was {
	font-size: 18px;
	color: #9ca3af;
	text-decoration: line-through;
}

.fs-pv-price-now {
	font-size: 28px;
	font-weight: 700;
	color: #dc2626;
}

.fs-badge-instock {
	color: #15803d;
	background: #dcfce7;
}

.fs-pv-cats {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 4px;
}

.fs-pv-cat {
	font-size: 11px;
	background: #f3f4f6;
	color: #6b7280;
	padding: 3px 10px;
	border-radius: 999px;
}

.fs-pv-description {
	margin-top: 8px;
	font-size: 14px;
	color: var(--fs-text);
	line-height: 1.6;
}

.fs-pv-description p {
	margin: 0 0 12px 0;
}

.fs-pv-description p:last-child {
	margin-bottom: 0;
}

/* Sticky action bar at the bottom of the detail view */
.fs-pv-actions {
	position: sticky;
	bottom: 0;
	background: var(--fs-bg, #ffffff);
	padding: 16px 0 0 0;
	margin-top: 24px;
	border-top: 1px solid #e5e7eb;
	display: flex;
	gap: 12px;
}

.fs-pv-btn {
	flex: 1;
	padding: 12px 20px;
	border-radius: 10px;
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
	text-align: center;
	transition: all 0.15s;
	cursor: pointer;
	border: none;
	font-family: inherit;
}

.fs-pv-btn-primary {
	background: var(--fs-primary);
	color: white;
}

.fs-pv-btn-primary:hover {
	opacity: 0.9;
	transform: translateY(-1px);
}

.fs-pv-btn-secondary {
	background: white;
	color: var(--fs-text);
	border: 1px solid #e5e7eb;
}

.fs-pv-btn-secondary:hover {
	border-color: var(--fs-primary);
	color: var(--fs-primary);
}

/* Similar products grid inside the detail view */
.fs-pv-similar {
	margin-top: 40px;
	padding-top: 24px;
	border-top: 1px solid #e5e7eb;
}

.fs-pv-section-title {
	font-size: 14px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: #6b7280;
	margin: 0 0 16px 0;
}

.fs-pv-similar-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
	gap: 12px;
}

.fs-pv-similar-item {
	display: flex;
	flex-direction: column;
	padding: 12px;
	background: white;
	border: 1px solid #e5e7eb;
	border-radius: 10px;
	cursor: pointer;
	transition: border-color 0.15s, transform 0.15s;
	font-family: inherit;
	font-size: inherit;
	text-align: left;
}

.fs-pv-similar-item:hover {
	border-color: var(--fs-primary);
	transform: translateY(-2px);
}

.fs-pv-similar-img {
	aspect-ratio: 1;
	background: #f9fafb;
	border-radius: 8px;
	overflow: hidden;
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.fs-pv-similar-img img {
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.fs-pv-similar-title {
	font-size: 13px;
	font-weight: 500;
	color: var(--fs-text);
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.fs-pv-similar-price {
	font-size: 13px;
	font-weight: 600;
	color: var(--fs-primary);
	margin-top: 4px;
}

/* Empty state with suggestions */
.fs-empty {
	display: none;
	text-align: center;
	padding: 60px 20px;
	color: #6b7280;
}

.fs-empty-inner {
	max-width: 480px;
	margin: 0 auto;
}

.fs-empty-icon {
	width: 48px;
	height: 48px;
	color: #d1d5db;
	margin-bottom: 16px;
}

.fs-empty-headline {
	font-size: 18px;
	color: var(--fs-text);
	margin: 0 0 4px 0;
}

.fs-empty-headline strong {
	color: var(--fs-primary);
	font-weight: 600;
}

.fs-empty-clear {
	margin-top: 12px;
	padding: 8px 16px;
	background: var(--fs-primary);
	color: white;
	border: none;
	border-radius: 6px;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
}

.fs-empty-clear:hover {
	opacity: 0.9;
}

.fs-suggestions {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	justify-content: center;
	margin-top: 20px;
}

.fs-suggestion-chip {
	padding: 6px 12px;
	background: #f3f4f6;
	border: 1px solid #e5e7eb;
	border-radius: 999px;
	font-size: 13px;
	color: var(--fs-text);
	cursor: pointer;
	transition: all 0.15s;
}

.fs-suggestion-chip:hover {
	background: var(--fs-primary);
	color: white;
	border-color: var(--fs-primary);
}

.fs-suggestion-chip-cat::before {
	content: "# ";
	color: #9ca3af;
}

.fs-suggestion-chip-cat:hover::before {
	color: rgba(255, 255, 255, 0.7);
}

/* Responsive — mobile rules for elements that survived the Doofinder
   rework (drawer backdrop, body padding, search input sizing). The
   facets-panel drawer + mobile-facets-btn live in the Doofinder block
   further down. */
@media (max-width: 768px) {
	/* Backdrop that dims the main content when the facets drawer is open */
	.fs-sidebar-backdrop {
		display: block;
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.45);
		z-index: 9;
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.22s ease;
	}

	.fs-sidebar-backdrop.is-open {
		opacity: 1;
		pointer-events: auto;
	}

	.fs-body {
		padding: 0;
	}

	.fs-search-container {
		padding: 0 12px;
	}

	.fs-search-input {
		font-size: 16px; /* prevents iOS zoom-on-focus */
	}

	/* Parts grid inside fitment detail view — keep it 2-col on phone */
	.fs-similar-grid {
		grid-template-columns: repeat(2, 1fr) !important;
		gap: 10px;
	}

	/* Product detail view padding */
	.fs-product-view,
	.fs-fitment-view {
		padding: 16px 16px 80px 16px;
	}

	/* Tighten the fitment parts list on phones — hide the image to give
	   the title/price more room, and shrink the plate badge. */
	.fs-fp-row {
		grid-template-columns: auto 1fr auto;
		gap: 10px;
		padding: 10px 12px;
	}
	.fs-fp-image {
		display: none;
	}
	.fs-fd-title {
		font-size: 20px;
	}
	.fs-fd-header {
		margin-bottom: 16px;
	}
	.fs-fd-title-block {
		padding-bottom: 14px;
		margin-bottom: 14px;
	}
	.fs-fd-plate {
		padding: 10px;
		margin-bottom: 16px;
	}
	.fs-fd-plate img {
		max-height: 360px;
	}
}

@media (max-width: 480px) {
	.fs-section-items {
		grid-template-columns: 1fr;
		gap: 8px;
	}

	.fs-result-card {
		padding: 10px;
	}

	.fs-result-title {
		font-size: 13px;
	}

	/* Single-column product detail on very narrow screens */
	.fs-pv-body {
		grid-template-columns: 1fr !important;
		gap: 16px;
	}

	.fs-similar-grid {
		grid-template-columns: 1fr !important;
	}
}

/* ═══════════════════════════════════════════════════════════════════════
   DOOFINDER-STYLE LAYOUT (added 2026-04-24)
   These rules override the older fs-sidebar-left / fs-main / fs-sidebar-right
   layout. The old classes are no longer in the DOM but their styles remain
   harmlessly above. New layout: fs-initial-body (popular + rail) and
   fs-results-body (facets + grid) toggled by setBodyState().
   ═══════════════════════════════════════════════════════════════════════ */

/* Override: search input becomes a pill with red focus border */
.fs-search-input {
	flex: 1;
	padding: 12px 24px;
	font-size: 18px;
	border: 2px solid transparent;
	background: #ffffff;
	border-radius: 999px;
	color: var(--fs-text);
	transition: border-color 0.15s ease;
}
.fs-search-input:focus {
	border-color: var(--fs-primary);
	outline: none;
}

/* Reusable utility — uppercase, letter-spaced section headings */
.fs-uppercase-title {
	font-family: inherit;
	font-size: 13px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: var(--fs-text);
	margin: 0;
}

/* Reusable utility — uppercase red text-link (DELETE ALL, VIEW MORE, etc.) */
.fs-action-link {
	background: transparent;
	border: none;
	cursor: pointer;
	font: inherit;
	font-size: 13px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: var(--fs-primary);
	padding: 4px 0;
}
.fs-action-link:hover { text-decoration: underline; }

/* Body container — replaces the old grid layout entirely */
.fs-body {
	display: block;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	background: #ffffff;
	padding: 0;
}

/* ─── INITIAL STATE (empty input) ─── */
/* Use full viewport width — Popular Searches stays narrow on the left so the
   Recommended Products rail can flow across the full available width. */
.fs-initial-body {
	display: grid;
	grid-template-columns: 200px 1fr;
	gap: 32px;
	max-width: 100%;
	margin: 0;
	padding: 32px 24px;
	min-height: 100%;
}

.fs-popular-column {
	min-width: 0;
}

.fs-popular-list {
	list-style: none;
	margin: 16px 0 0 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.fs-popular-link {
	background: none;
	border: none;
	padding: 4px 0;
	cursor: pointer;
	color: var(--fs-text);
	font-size: 15px;
	text-align: left;
	line-height: 1.5;
}
.fs-popular-link:hover { color: var(--fs-primary); }

.fs-recommended-section { min-width: 0; }

.fs-rail-wrapper {
	position: relative;
	margin-top: 16px;
}

.fs-rail-track {
	display: flex;
	gap: 16px;
	overflow-x: auto;
	scroll-snap-type: x proximity;
	scroll-behavior: smooth;
	padding: 4px 4px 8px;
	/* Hide scrollbar — arrows are the navigation */
	scrollbar-width: thin;
	scrollbar-color: #e5e7eb transparent;
}
.fs-rail-track::-webkit-scrollbar { height: 8px; }
.fs-rail-track::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }

.fs-rail-item {
	flex: 0 0 200px;
	scroll-snap-align: start;
}

.fs-rail-arrow {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	box-shadow: 0 2px 8px rgba(0,0,0,0.1);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--fs-text);
	z-index: 2;
	transition: opacity 0.15s ease, transform 0.12s ease;
}
.fs-rail-arrow:hover { transform: translateY(-50%) scale(1.05); }
.fs-rail-arrow svg { width: 18px; height: 18px; }
.fs-rail-arrow-left { left: -18px; }
.fs-rail-arrow-right { right: -18px; }

/* ─── RESULTS STATE (query active) ─── */
.fs-results-body {
	display: grid;
	grid-template-columns: 280px 1fr;
	gap: 32px;
	max-width: 1400px;
	margin: 0 auto;
	padding: 24px;
	min-height: 100%;
}

.fs-facets-panel {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.fs-facet {
	border-bottom: 1px solid #e5e7eb;
	padding-bottom: 16px;
}
.fs-facet:last-child { border-bottom: none; }

.fs-facet-toggle {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	font: inherit;
	color: inherit;
}
.fs-facet-chevron {
	width: 16px;
	height: 16px;
	transition: transform 0.15s ease;
	color: #6b7280;
}
.fs-facet-toggle[aria-expanded="false"] .fs-facet-chevron {
	transform: rotate(180deg);
}
.fs-facet-toggle[aria-expanded="false"] + .fs-facet-body {
	display: none;
}

.fs-facet-body {
	margin-top: 12px;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.fs-facet-search-wrap {
	position: relative;
}
.fs-facet-search-icon {
	position: absolute;
	left: 10px;
	top: 50%;
	transform: translateY(-50%);
	width: 14px;
	height: 14px;
	color: #9ca3af;
	pointer-events: none;
}
.fs-facet-search {
	width: 100%;
	padding: 8px 12px 8px 32px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	font-size: 13px;
	background: #f9fafb;
	color: var(--fs-text);
}
.fs-facet-search:focus {
	outline: none;
	border-color: var(--fs-primary);
	background: #ffffff;
}

.fs-facet-terms {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.fs-facet-term { line-height: 1.4; }

.fs-facet-term-label {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	padding: 6px 0;
	font-size: 14px;
	color: var(--fs-text);
}
.fs-facet-term-label:hover { color: var(--fs-primary); }

.fs-facet-term-checkbox {
	width: 16px;
	height: 16px;
	cursor: pointer;
	accent-color: var(--fs-primary);
	flex-shrink: 0;
}

.fs-facet-term-name {
	flex: 1;
	letter-spacing: 0.04em;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.fs-facet-term-count {
	color: #6b7280;
	font-size: 13px;
	font-variant-numeric: tabular-nums;
	flex-shrink: 0;
}

.fs-facet-footer {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 4px;
	font-size: 12px;
	color: #6b7280;
}
.fs-facet-count-label { padding: 4px 0; }

.fs-instock-toggle {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	font-size: 14px;
	font-weight: 500;
}
.fs-instock-toggle input[type="checkbox"] {
	accent-color: var(--fs-primary);
}

.fs-results-section {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.fs-results-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding-bottom: 8px;
	border-bottom: 1px solid #f3f4f6;
	flex-wrap: wrap;
}

.fs-results-count {
	font-size: 14px;
	color: #6b7280;
	margin-right: auto;
}
.fs-results-count strong {
	color: var(--fs-text);
	font-size: 18px;
	font-weight: 700;
	margin-right: 4px;
}

/* Sort dropdown — right-aligned in the results header. */
.fs-sort-wrap {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	color: #6b7280;
}
.fs-sort-label {
	text-transform: uppercase;
	letter-spacing: 0.04em;
	font-weight: 600;
}
.fs-sort-select {
	background: transparent;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	padding: 6px 28px 6px 10px;
	font: inherit;
	font-size: 13px;
	color: var(--fs-text);
	cursor: pointer;
	appearance: none;
	background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
	background-repeat: no-repeat;
	background-position: right 8px center;
	background-size: 14px;
}
.fs-sort-select:focus {
	outline: none;
	border-color: var(--fs-primary);
}

/* Override results grid — Birch uses 4 cols at desktop (was 3) per Bec's feedback,
   fits more products on screen. Mobile breakpoints below step it down to 2 and 1. */
.fs-results-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 16px;
	min-height: 200px;
}

/* Step down to 3 columns at narrower desktop where 4 would crush the cards. */
@media (max-width: 1200px) {
	.fs-results-grid { grid-template-columns: repeat(3, 1fr); }
}

/* Mobile: drawer-style facets, button to open */
.fs-mobile-facets-btn,
.fs-mobile-facets-close {
	display: none;
	background: none;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 8px 12px;
	cursor: pointer;
	font: inherit;
	font-size: 13px;
	color: var(--fs-text);
	align-items: center;
	gap: 6px;
}
.fs-mobile-facets-btn svg,
.fs-mobile-facets-close svg {
	width: 16px;
	height: 16px;
}
.fs-mobile-facets-close {
	position: absolute;
	top: 16px;
	right: 16px;
	border: none;
	width: 32px;
	height: 32px;
	padding: 0;
	justify-content: center;
}

@media (max-width: 768px) {
	.fs-initial-body {
		grid-template-columns: 1fr;
		padding: 16px;
		gap: 24px;
	}
	.fs-results-body {
		grid-template-columns: 1fr;
		padding: 16px;
		gap: 16px;
	}
	.fs-facets-panel {
		position: fixed;
		top: 0;
		left: 0;
		bottom: 0;
		width: 280px;
		max-width: 85vw;
		background: #ffffff;
		padding: 56px 16px 16px;
		transform: translateX(-100%);
		transition: transform 0.25s ease;
		z-index: 10;
		overflow-y: auto;
		box-shadow: 4px 0 16px rgba(0,0,0,0.1);
	}
	.fs-facets-panel.is-open { transform: translateX(0); }
	.fs-mobile-facets-btn { display: inline-flex; }
	.fs-mobile-facets-close { display: inline-flex; }
	.fs-results-grid { grid-template-columns: repeat(2, 1fr); }
	.fs-rail-arrow-left { left: 0; }
	.fs-rail-arrow-right { right: 0; }

	/* Centring fix: chip bars must inherit the same horizontal padding
	   as the search container so chips align with the input below.
	   Without this, chips visually float left of where they should,
	   which Ryan flagged as "page isn't centred" on mobile. */
	.fs-recent-bar,
	.fs-suggest-bar {
		padding: 0 12px;
		box-sizing: border-box;
	}

	/* Close button — bigger, clearer, always tappable. iOS HIG min 44px.
	   Without a guaranteed-visible close X, mobile users get "stuck" in
	   the overlay because there's no Esc key. */
	.fs-close-btn {
		width: 44px;
		height: 44px;
		flex-shrink: 0;
		background: #f3f4f6;
		border-radius: 50%;
	}
	.fs-close-btn svg {
		width: 22px;
		height: 22px;
	}

	/* Header padding tweak — slightly less so input has more room next
	   to the logo + close button on narrow screens. */
	.fs-header { padding: 12px 0; }

	/* Sort dropdown — wraps under the count on narrow screens to avoid
	   the awkward squish. */
	.fs-results-header {
		flex-wrap: wrap;
	}
	.fs-sort-wrap {
		flex-basis: 100%;
		justify-content: flex-end;
	}
}

@media (max-width: 480px) {
	.fs-results-grid { grid-template-columns: 1fr; }
	.fs-rail-item { flex-basis: 180px; }
}
`
